import React, { useEffect, useState } from "react";
import cn from "classnames";
import { template } from "lodash";
import { useTranslation } from "react-i18next";
import { Button, Form, Message, toaster } from "rsuite";
import { TypeAttributes } from "rsuite/esm/internals/types";

import constants from "utils/constants";
import { formatUtcDateTimeWithNameOfMonth } from "utils/date-helper";
import { tenantFeatureSettingDefinitions } from "utils/tenant-workflows/tenant-workflows-definitions";
import {
    ITenantFeatureSettingWorkFlow,
    TenantFeatureSettingWorkFlowType,
    WorkflowModalType,
    WorkflowStatus,
} from "utils/tenant-workflows/tenant-workflows-types";

import {
    IErpInfo,
    SecretExpiration,
} from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import { SecretExpirationStatus } from "store/environments/tenants/tenants-types";

import { toggleTenantFeatureSetting } from "api/environments/tenants/tenant-feature-settings-api";

import ConfirmModal, {
    ConfirmationMode,
} from "components/Common/ConfirmModal/ConfirmModal";
import ProcessModalButton from "components/WorkflowLog/ProcessModalButton/ProcessModalButton";

import css from "./ClientSecretFormGroup.module.css";

interface ComponentProps {
    environmentMoniker: string;
    tenantMoniker: string;
    keyVaultAppRegistrationSecretRotationWorkflow: ITenantFeatureSettingWorkFlow | null;
    erpInfo: IErpInfo;
    setIsTenantFetched: (isFetched: boolean) => void;
}
const ClientSecretFormGroup: React.FC<ComponentProps> = ({
    environmentMoniker,
    tenantMoniker,
    keyVaultAppRegistrationSecretRotationWorkflow,
    erpInfo,
    setIsTenantFetched,
}: ComponentProps) => {
    const { t } = useTranslation();
    const [isRefreshSecretDisabled, setRefreshSecretDisabled] =
        useState<boolean>(false);
    const [confirmVisible, setConfirmVisible] = useState<boolean>(false);

    const handleRefreshClick = () => {
        setConfirmVisible(true);
    };

    const handleCancel = () => {
        setConfirmVisible(false);
    };

    const handleConfirm = () => {
        setConfirmVisible(false);
        setRefreshSecretDisabled(true);

        const settingDefinition =
            tenantFeatureSettingDefinitions[
                TenantFeatureSettingWorkFlowType
                    .KeyVaultAppRegistrationSecretRotation
            ];

        toggleTenantFeatureSetting(
            environmentMoniker,
            tenantMoniker,
            settingDefinition.urlParameterValue,
            {
                settingName: settingDefinition.urlParameterValue,
                toggleValue: true,
                data: null,
            }
        ).then((response) => {
            const [, status] = response;
            const isResponseStatusCodeExpected =
                status === constants.statusCode.NoContent ||
                status === constants.statusCode.OK;

            if (isResponseStatusCodeExpected) {
                toaster.push(
                    <Message type="success" showIcon>
                        {t(
                            "tenantConfig.d365InformationPage.clientSecret.startedSuccessfully"
                        )}
                    </Message>,
                    {
                        duration: constants.alertDurationMsec,
                    }
                );
            } else {
                toaster.push(
                    <Message type="error" showIcon>
                        {t(
                            "tenantConfig.d365InformationPage.clientSecret.startedFailure"
                        )}
                    </Message>,
                    {
                        duration: constants.alertDurationMsec,
                    }
                );
            }
            setRefreshSecretDisabled(false);
        });
    };

    const handleRefreshAfterTerminate = () => {
        setIsTenantFetched(false);
    };

    const getExpirationMessageProps = (
        secretExpiration: SecretExpiration,
        message: string
    ): {
        type: TypeAttributes.Status;
        description: string;
        show: boolean;
    } => {
        const description = template(message)({
            expirationDays: secretExpiration.timeInDays,
            expirationDateOffset: secretExpiration.rotationDate
                ? formatUtcDateTimeWithNameOfMonth(
                      new Date(secretExpiration.rotationDate)
                  )
                : null,
        });

        switch (secretExpiration.status) {
            case SecretExpirationStatus.Normal:
                return {
                    type: "info",
                    description,
                    show: false,
                };
            case SecretExpirationStatus.Warning:
                return {
                    type: "warning",
                    description,
                    show: true,
                };
            case SecretExpirationStatus.Error:
                return {
                    type: "error",
                    description,
                    show: true,
                };
            default:
                return {
                    type: "info",
                    description: "",
                    show: false,
                };
        }
    };

    const appRegExpirationMessageProps = getExpirationMessageProps(
        erpInfo.appRegSecretExpiration,
        t("tenantConfig.d365InformationPage.clientSecret.message")
    );

    const erpSecretExpirationMessageProps = getExpirationMessageProps(
        erpInfo.axSecretExpiration,
        erpInfo.axSecretExpiration.status &&
            erpInfo.axSecretExpiration.status === SecretExpirationStatus.Error
            ? t("tenantConfig.d365InformationPage.erpSecretExpiredMessage")
            : t("tenantConfig.d365InformationPage.erpSecretExpirationMessage")
    );

    const appRegSecretExpirationShown =
        erpInfo.appRegSecretExpiration.timeInDays !== null &&
        erpInfo.appRegSecretExpiration.status !== null;
    const axSecretExpirationShown =
        erpInfo.axSecretExpiration.timeInDays !== null &&
        erpInfo.axSecretExpiration.status !== null;

    const isClientSecretFormGroupShown =
        appRegSecretExpirationShown || axSecretExpirationShown;

    useEffect(() => {
        if (
            keyVaultAppRegistrationSecretRotationWorkflow &&
            (keyVaultAppRegistrationSecretRotationWorkflow.status ===
                WorkflowStatus.Pending ||
                keyVaultAppRegistrationSecretRotationWorkflow.status ===
                    WorkflowStatus.Running ||
                keyVaultAppRegistrationSecretRotationWorkflow.status ===
                    WorkflowStatus.NotStarted)
        ) {
            setRefreshSecretDisabled(true);
        } else {
            setRefreshSecretDisabled(false);
        }
    }, [keyVaultAppRegistrationSecretRotationWorkflow?.status]);

    return isClientSecretFormGroupShown ? (
        <div className={"general-configurations__client-secret-section"}>
            <ConfirmModal
                open={confirmVisible}
                message={t(
                    "tenantConfig.d365InformationPage.clientSecret.confirmMessage"
                )}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                mode={ConfirmationMode.Destructive}
            />
            {appRegSecretExpirationShown && (
                <Form.Group
                    className={cn(
                        "general-configurations__grid-item",
                        css.gridItemClientSecret
                    )}
                >
                    <Form.ControlLabel
                        className={"general-configurations__label"}
                    >
                        {t(
                            "tenantConfig.d365InformationPage.clientSecret.title"
                        )}
                        <Form.HelpText
                            className={"general-configurations__help"}
                            tooltip
                        >
                            {t(
                                "tenantConfig.d365InformationPage.clientSecret.tooltip"
                            )}
                        </Form.HelpText>
                    </Form.ControlLabel>
                    <div className={css.rotateRow}>
                        <Form.Control
                            name="clientSecretRotate"
                            accepter={Button}
                            appearance="ghost"
                            onClick={() => handleRefreshClick()}
                            className={css.rotateButton}
                            disabled={isRefreshSecretDisabled}
                        >
                            {t(
                                "tenantConfig.d365InformationPage.clientSecret.rotateBtn"
                            )}
                        </Form.Control>
                        {keyVaultAppRegistrationSecretRotationWorkflow && (
                            <ProcessModalButton
                                workflow={
                                    keyVaultAppRegistrationSecretRotationWorkflow
                                }
                                workflowType={WorkflowModalType.Feature}
                                triggerRefresh={handleRefreshAfterTerminate}
                            />
                        )}
                    </div>
                    {appRegExpirationMessageProps.show && (
                        <Message
                            className={css.clientSecretMessage}
                            showIcon
                            {...appRegExpirationMessageProps}
                        >
                            {appRegExpirationMessageProps.description}
                        </Message>
                    )}
                </Form.Group>
            )}
            {axSecretExpirationShown && (
                <Form.Group
                    className={cn(
                        "general-configurations__grid-item",
                        css.gridItemClientSecret
                    )}
                >
                    {erpSecretExpirationMessageProps.show && (
                        <Message
                            className={css.clientSecretMessage}
                            showIcon
                            {...erpSecretExpirationMessageProps}
                        >
                            {erpSecretExpirationMessageProps.description}
                        </Message>
                    )}
                </Form.Group>
            )}
        </div>
    ) : null;
};

export default ClientSecretFormGroup;
